<template>
  <teleport to="body">
    <div
      v-if="state.visible"
      v-drag-window="{ resizable: true, minWidth: 420, minHeight: 300 }"
      class="aliyun-live-player-window"
    >
      <div class="drag-title aliyun-live-player-window__title">
        <div class="aliyun-live-player-window__heading">
          <span>阿里云直播</span>
          <span aria-hidden="true">·</span>
          <span class="aliyun-live-player-window__sn">{{ state.streamName }}</span>
        </div>
        <div class="aliyun-live-player-window__actions">
          <button type="button" class="drag-ignore" title="重新播放" @click="retryPlayback">
            <ReloadOutlined />
          </button>
          <button type="button" class="drag-ignore" title="关闭播放器" @click="closePlayer">
            <CloseOutlined />
          </button>
        </div>
      </div>
      <div class="aliyun-live-player-window__body">
        <video ref="videoRef" playsinline muted controls></video>
        <div v-if="statusText" class="aliyun-live-player-window__status">{{ statusText }}</div>
      </div>
      <div class="drag-resize-handle aliyun-live-player-window__resize"></div>
    </div>
  </teleport>
</template>

<script lang="ts" setup>
import mpegts from 'mpegts.js'
import { nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { CloseOutlined, ReloadOutlined } from '@ant-design/icons-vue'
import { aliyunLivePlayerState as state, closeAliyunLivePlayer } from './aliyun-live-player-state'
import { destroyMpegtsPlayer } from './mpegts-player-lifecycle'

const videoRef = ref<HTMLVideoElement>()
const statusText = ref('')
let player: mpegts.Player | null = null

watch(
  () => [state.visible, state.playUrl],
  async ([visible]) => {
    destroyPlayer()
    if (!visible || !state.playUrl) {
      return
    }
    await nextTick()
    startPlayback()
  },
  { immediate: true }
)

onBeforeUnmount(destroyPlayer)

function startPlayback () {
  if (!videoRef.value) {
    return
  }
  if (!mpegts.isSupported()) {
    statusText.value = '当前浏览器不支持 HTTP-FLV 播放'
    return
  }

  statusText.value = '正在连接阿里云直播...'
  player = mpegts.createPlayer({
    type: 'flv',
    isLive: true,
    url: state.playUrl,
    cors: true,
  }, {
    enableStashBuffer: false,
    lazyLoad: false,
    autoCleanupSourceBuffer: true,
    liveBufferLatencyChasing: true,
  })
  player.attachMediaElement(videoRef.value)
  player.on(mpegts.Events.ERROR, (_type: unknown, detail: unknown) => {
    statusText.value = `阿里云直播播放失败: ${String(detail || '未知错误')}`
  })
  player.on(mpegts.Events.LOADING_COMPLETE, () => {
    statusText.value = '直播流已结束'
  })
  player.load()
  player.play()
    .then(() => {
      statusText.value = ''
    })
    .catch(() => {
      statusText.value = '点击播放器开始播放'
    })
}

function retryPlayback () {
  destroyPlayer()
  startPlayback()
}

function closePlayer () {
  destroyPlayer()
  closeAliyunLivePlayer()
}

function destroyPlayer () {
  if (player) {
    destroyMpegtsPlayer(player)
    player = null
  }
  statusText.value = ''
}
</script>

<style lang="scss">
.aliyun-live-player-window {
  position: fixed;
  z-index: 1300;
  top: 64px;
  left: 24px;
  width: 640px;
  height: 420px;
  display: flex;
  flex-direction: column;
  color: #fff;
  background: #111827;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 8px;
  box-shadow: 0 14px 36px rgba(0, 0, 0, 0.42);
  overflow: hidden;
}

.aliyun-live-player-window__title {
  flex: 0 0 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 10px 0 14px;
  background: rgba(15, 23, 42, 0.96);
}

.aliyun-live-player-window__heading,
.aliyun-live-player-window__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.aliyun-live-player-window__sn {
  max-width: 320px;
  color: #b7c3d0;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.aliyun-live-player-window__actions button {
  width: 28px;
  height: 28px;
  padding: 0;
  color: #fff;
  background: transparent;
  border: 0;
  border-radius: 4px;
  cursor: pointer;
}

.aliyun-live-player-window__actions button:hover {
  background: rgba(255, 255, 255, 0.12);
}

.aliyun-live-player-window__body {
  position: relative;
  flex: 1;
  min-height: 0;
  background: #000;
}

.aliyun-live-player-window__body video {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
  background: #000;
}

.aliyun-live-player-window__status {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: #d1d5db;
  background: rgba(0, 0, 0, 0.55);
  pointer-events: none;
}

.aliyun-live-player-window__resize {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 18px;
  height: 18px;
  cursor: nwse-resize;
}
</style>
